import React from 'react';
import { useTranslation } from 'react-i18next';
import { AlertCircle, Clock } from 'lucide-react';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import type { AgentReadiness } from '@/utils/subscriptionUtils';

interface AgentReadinessBadgeProps {
  readiness: AgentReadiness;
  /** True when the agent has never had a subscription, i.e. it was just created. */
  isNew?: boolean;
  className?: string;
}

const AgentReadinessBadge: React.FC<AgentReadinessBadgeProps> = ({
  readiness,
  isNew = false,
  className = '',
}) => {
  const { t } = useTranslation();

  if (readiness === 'ready') return null;

  const isWaiting = readiness === 'waiting';
  const Icon = isWaiting ? Clock : AlertCircle;

  const colors = isWaiting
    ? {
        bg: 'bg-amber-50 dark:bg-amber-900/20',
        border: 'border-amber-200 dark:border-amber-800',
        icon: 'text-amber-600 dark:text-amber-400',
      }
    : {
        bg: 'bg-blue-50 dark:bg-blue-900/20',
        border: 'border-blue-200 dark:border-blue-800',
        icon: 'text-blue-600 dark:text-blue-400',
      };

  const label = isWaiting
    ? t('agent.readiness.waiting.label.translation', 'Pending approval')
    : isNew
      ? t('agent.readiness.setup.label.translation', 'Setup needed')
      : t('agent.readiness.expired.label.translation', 'Subscription expired');

  const description = isWaiting
    ? t(
        'agent.readiness.waiting.description.translation',
        'Your subscription request is being reviewed. This agent cannot take calls yet.'
      )
    : isNew
      ? t(
          'agent.readiness.setup.description.translation',
          'This agent needs an active subscription before it can take calls.'
        )
      : t(
          'agent.readiness.expired.description.translation',
          'This agent’s subscription has expired, so it is no longer taking calls.'
        );

  return (
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger asChild>
          <div
            role="status"
            aria-label={`${label}. ${description}`}
            onClick={event => event.stopPropagation()}
            className={`flex flex-shrink-0 items-center justify-center rounded-full border ${colors.bg} ${colors.border} p-1.5 ${className}`}
          >
            <Icon className={`h-4 w-4 ${colors.icon}`} aria-hidden={true} />
          </div>
        </TooltipTrigger>
        <TooltipContent className="max-w-[16rem]">
          <p className="font-semibold">{label}</p>
          <p className="text-xs">{description}</p>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
};

export default AgentReadinessBadge;
